import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Params } from '@angular/router';
import { LongActionButtonComponent, OverlaySpinnerComponent, PaginationComponent } from '@components';
import { FilterChipsComponent, FilterComponent, FilterHelper, GridLoadingComponent } from '@components/data-grid';
import { IconoirCloudDownload, IconoirFilterList } from '@ey-xd/motif-icon';
import {
  MotifButtonModule,
  MotifDatePickerModule,
  MotifFormsModule,
  MotifIconModule,
  MotifModal,
  MotifModalConfig,
  MotifModalModule,
  MotifNativeDateTimeModule,
  MotifPaginationModule,
  MotifProgressLoaderModule,
  MotifTableModule,
  MotifTabNavigationModule,
} from '@ey-xd/ng-motif';
import { Option } from '@models/pagination-select-option';
import {
  ConnectorStatus,
  FilterGroup,
  GridData,
  IDataConnector,
  MAX_COLUMN_WIDTH,
  PageSizeOptions,
  PropertyFilter,
  RowDataDefinition,
  sortDirectionType,
} from '@models';
import { FieldType, FilterCriterion, FilterModalData } from '@models/filter.model';
import { TranslateModule } from '@ngx-translate/core';
import { DataGridHelperService } from '@services';
import { ColDef, GridApi, GridReadyEvent, RowSelectionOptions, SortChangedEvent } from 'ag-grid-community';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  from,
  map,
  Observable,
  of,
  Subject,
} from 'rxjs';
import { delay, filter, mergeMap, pairwise, startWith, switchMap, take } from 'rxjs/operators';
import { GridButton } from './grid-button/grid-button';
import { GridButtonComponent } from './grid-button/grid-button.component';

@Component({
  selector: 'app-data-grid',
  standalone: true,
  imports: [
    CommonModule,
    MotifTableModule,
    MotifIconModule,
    MotifPaginationModule,
    MotifButtonModule,
    TranslateModule,
    MotifTabNavigationModule,
    MotifProgressLoaderModule,
    PaginationComponent,
    MotifDatePickerModule,
    MotifNativeDateTimeModule,
    MotifModalModule,
    MotifFormsModule,
    ReactiveFormsModule,
    FilterChipsComponent,
    LongActionButtonComponent,
    OverlaySpinnerComponent,
    GridButtonComponent,
  ],
  templateUrl: './data-grid.component.html',
  styleUrl: './data-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataGridComponent implements OnInit {
  readonly exportIcon = IconoirCloudDownload;

  columns = input.required<ColDef[]>();
  dataConnector = input.required<IDataConnector>();
  showPagination = input.required<boolean>();
  hidden = input.required<boolean>();
  export$ = input<Observable<boolean> | undefined>(undefined);
  hideSearch = input<boolean>(false);
  defaultFilters = input<FilterGroup[]>([]);
  rowSelectionOptions = input<RowSelectionOptions | undefined>(undefined);
  gridButtons = input<GridButton[]>([]);
  maxColumnWidth = input<number>(MAX_COLUMN_WIDTH);
  selectedRows = output<object[]>();
  rowsCount = output<number>();
  currentParams = output<Params>();

  public readonly numberOfRows = signal<number>(0);

  currentPage = signal<number>(1);
  numberOfPages = signal<number>(0);
  rows = signal<RowDataDefinition[]>([]);
  filters = signal<FilterGroup[]>([]);
  chipsFilters = signal<PropertyFilter[]>([]);

  pageSizeOptions: Option[] = PageSizeOptions;
  currentPageSize: Option = this.pageSizeOptions[0];

  filterCriteria = input.required<FilterCriterion[]>();
  filterCriteria$ = toObservable(this.filterCriteria);

  dataGridHelper: DataGridHelperService = inject(DataGridHelperService);
  readonly modal = inject(MotifModal);
  public filterListIcon = IconoirFilterList;
  public searchControl: FormControl = new FormControl<string>('');
  public isLoading = signal<boolean>(true);
  overlayNoRowsTemplate = signal(this.dataGridHelper.getNoRowsTemplate());

  public readonly gridOptions = computed(() => ({
    suppressRowHoverHighlight: true,
    onFirstDataRendered: () => {
      this.#columnWidthCalcTrigged$.next();
    },
    onRowDataUpdated: () => {
      this.#columnWidthCalcTrigged$.next();
    },
    loadingOverlayComponent: GridLoadingComponent,
    rowSelection: this.rowSelectionOptions(),
    enableCellTextSelection: true,
    overlayNoRowsTemplate: this.overlayNoRowsTemplate(),
  }));

  public searchForm: FormGroup = new FormGroup({
    search: this.searchControl,
  });
  #gridApi: GridApi | null = null;
  #destroyRef: DestroyRef = inject(DestroyRef);

  readonly #columnWidthCalcTrigged$ = new BehaviorSubject<void>(undefined);
  readonly #searchControlReset$ = new Subject<void>();
  readonly hidden$ = toObservable(this.hidden);
  readonly showFilter = computed(() => this.filterCriteria() && this.filterCriteria().length > 0);
  readonly showSearch = computed(() => !this.hideSearch());

  @ViewChild('searchInput', { static: false }) searchInput!: ElementRef<HTMLElement>;

  ngOnInit(): void {
    this.#initializeDefaultFilters();
    this.#initializeGridSubscriptions();
    this.#initializeSearchControl();
    this.#initializeSort();
    this.#initializePageSize();
    this.#preloadFilterOptions();
  }

  #initializeDefaultFilters(): void {
    if (this.defaultFilters().length > 0 && this.filters().length === 0) {
      this.filters.set(this.defaultFilters());
      this.chipsFilters.set(
        this.defaultFilters().flatMap((filter: FilterGroup): PropertyFilter[] => filter.entityPropertyFilters)
      );
    }
  }

  public gridReady(params: GridReadyEvent): void {
    this.#gridApi = params.api;
  }

  public pageChanged(newPage: number): void {
    this.currentPage.set(newPage);
    this.dataConnector().onPageChange(newPage, this.currentPageSize.value);
  }

  public pageSizeChanged(newPageSize: Option): void {
    this.currentPage.set(1);
    this.currentPageSize = newPageSize;
    this.dataConnector().onPageChange(1, newPageSize.value);
  }

  public onSelectionChanged(): void {
    this.selectedRows.emit(this.#gridApi?.getSelectedRows() || []);
  }

  public onSortChanged(event: SortChangedEvent): void {
    this.currentPage.set(1);
    const sortColumns = this.#extractSortColumns(event);
    if (sortColumns?.sort) {
      this.dataConnector().onSort(sortColumns.field, sortColumns.sort as sortDirectionType);
    } else {
      this.dataConnector().onSort(null, null);
    }
  }

  public onFilterClick(): void {
    const filterModalData: FilterModalData = {
      filterCriteria: this.filterCriteria(),
      selectedValues: this.filters() as unknown as {
        key: string;
        field: string;
        values: string[];
      }[],
    };
    const filterModalConfig: MotifModalConfig<FilterModalData> = FilterHelper.getModalConfig(filterModalData);
    const filterModal = this.modal.open<FilterComponent, FilterModalData>(FilterComponent, filterModalConfig);
    filterModal
      .afterClosed()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(filterModalResults => {
        if (filterModalResults) {
          this.#setFilters(FilterHelper.fromModalResultsToFilterGroups(filterModalResults));
        }
      });
  }

  public onRemoveChip(propertyFilter: PropertyFilter[]): void {
    const updatedFilters = this.filters()
      .map(filter => ({
        ...filter,
        entityPropertyFilters: filter.entityPropertyFilters.filter(
          pf => !propertyFilter.some(removed => removed.propertyName === pf.propertyName && removed.value === pf.value)
        ),
      }))
      .filter(filter => filter.entityPropertyFilters.length > 0);

    this.#setFilters(updatedFilters);
  }

  public onRemoveAll() {
    this.#setFilters([]);
  }

  public deselectAll() {
    this.#gridApi?.deselectAll();
  }

  #setFilters(filters: FilterGroup[]): void {
    this.filters.set(filters);
    this.currentPage.set(1);

    const chipsFilters: PropertyFilter[] = filters.flatMap(
      (filter: FilterGroup): PropertyFilter[] => filter.entityPropertyFilters
    );
    this.chipsFilters.set(chipsFilters);

    this.dataConnector().onFilter(filters);
    this.dataConnector().refresh();
    this.dataConnector().setFilters(filters);
  }

  #initializeSearchControl(): void {
    this.#subscribeToObservable(
      this.#searchControlReset$.pipe(
        startWith(undefined),
        switchMap(() =>
          this.searchControl.valueChanges.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            map(value => (typeof value === 'string' ? value.trim() : '')),
            startWith(''),
            pairwise(),
            filter(([prev, curr]: [string, string]): boolean => {
              const prevLen = prev.length;
              const currLen = curr.length;
              return (
                (prev !== curr && prevLen >= 3 && currLen === prevLen) ||
                (prevLen > currLen && prevLen >= 3) ||
                (prevLen < currLen && currLen >= 3)
              );
            }),
            switchMap(([, value]: [string, string]): Observable<GridData> => {
              this.dataConnector().setStatus({ isLoading: true });
              this.searchControl.disable();
              return this.dataConnector().onSearch$(value.length >= 3 ? value : '');
            })
          )
        )
      ),
      (data: GridData): void => {
        this.#updateGridData({
          ...data,
        });
        this.dataConnector().setStatus({ isLoading: false });
        this.searchControl.enable();
        this.searchInput?.nativeElement?.focus();
      }
    );
  }

  #updateGridData(data: GridData): void {
    if (this.numberOfPages() !== data.totalPages) {
      this.numberOfPages.set(data.totalPages);
    }
    if (this.currentPage() > data.totalPages) {
      this.currentPage.set(1);
    }
    this.numberOfRows.set(data.rowCount);
    this.rowsCount.emit(data.rowCount);
    this.rows.set(data.rows);
    this.#gridApi?.updateGridOptions({
      overlayNoRowsTemplate: this.dataGridHelper.getNoRowsTemplate(this.#filtersAreSet(), this.#searchIsSet()),
    });
  }

  #subscribeToObservable<T>(observable: Observable<T>, callback: (value: T) => void): void {
    this.dataGridHelper.subscribeToObservable(observable, callback, this.#destroyRef);
  }

  #initializeGridSubscriptions(): void {
    this.#subscribeToObservable(this.dataConnector().getStatus$(), (status: ConnectorStatus): void => {
      this.isLoading.set(status.isLoading);
    });

    this.#subscribeToObservable(this.dataConnector().getRows$(), (data: GridData): void => {
      this.#updateGridData(data);
    });

    this.#subscribeToObservable(this.dataConnector().getDataParams$(), (params: Params): void => {
      this.currentParams.emit(params);
    });

    this.#subscribeToObservable(this.hidden$, (isHidden: boolean): void => {
      if (isHidden) {
        this.deselectAll();
      }
      this.#columnWidthCalcTrigged$.next();
    });

    this.#subscribeToObservable(this.#columnWidthCalcTrigged$.pipe(delay(50)), (): void => {
      this.#handleColumnWidths();
    });
  }

  #extractSortColumns(event: SortChangedEvent): { field: string; sort: string } | null {
    return this.dataGridHelper.extractSortColumns(event);
  }

  #handleColumnWidths(): void {
    if (!this.hidden()) {
      this.dataGridHelper.handleColumnWidths(this.#gridApi);
    }
  }

  #filtersAreSet(): boolean {
    return this.filters().length > 0;
  }

  #searchIsSet(): boolean {
    return this.searchControl.value && this.searchControl.value.length >= 0;
  }

  #initializeSort() {
    const sortBy = this.dataConnector().sortBy;
    const sortDirection = this.dataConnector().sortDirection;

    // clear other sorts
    this.columns()
      .filter(c => c.field !== sortBy)
      .forEach(c => (c.sort = undefined));

    this.columns()
      .filter(c => c.field === sortBy)
      .forEach(c => (c.sort = sortDirection as 'asc' | 'desc'));
  }

  #preloadFilterOptions(): void {
    this.filterCriteria$
      .pipe(
        filter(criteria => criteria.length > 0),
        debounceTime(100),
        map(criteria =>
          criteria.filter(
            c => (c.type === FieldType.Lookup || c.type === FieldType.MultipleLookup) && c.optionsRequest$
          )
        ),
        switchMap(criteria =>
          from(criteria).pipe(
            mergeMap(criterion =>
              criterion.optionsRequest$!('').pipe(
                take(1),
                map(options => ({ criterion, options })),
                catchError(() => of({ criterion, options: criterion.options ?? [] }))
              )
            )
          )
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(({ criterion, options }) => {
        criterion.options = options;
      });
  }

  #initializePageSize() {
    const pageSize = this.dataConnector().pageSize;
    const pageSizeOption = this.pageSizeOptions.find(ps => ps.value === pageSize);
    if (pageSizeOption) {
      this.currentPageSize = pageSizeOption;
    }
  }

  public reset() {
    this.dataConnector().setFilters([]);

    this.chipsFilters.set([]);
    this.filters.set([]);
    this.searchControl.setValue('', { emitEvent: false });
    this.searchControl.enable({ emitEvent: false });
    this.dataConnector().setSearchTerm('');
    this.#searchControlReset$.next();
    this.currentPage.set(1);
    this.numberOfPages.set(0);
    this.numberOfRows.set(0);
    this.rows.set([]);
    this.deselectAll();
    this.selectedRows.emit([]);
    this.dataConnector().setStatus({ isLoading: false });
  }
}
